import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const BulkCompleteModal = ({
  isOpen,
  onClose,
  tasks = [],
  workTypes = [],
  onBulkComplete,
  isSubmitting = false,
  showWorktypeSelection = false,
  title = 'Mark Tasks as Complete'
}) => {
  const [selectedTasksForCompletion, setSelectedTasksForCompletion] = useState([]);
  const [selectedWorktypeForCompletion, setSelectedWorktypeForCompletion] = useState('');
  const [filteredTasksForCompletion, setFilteredTasksForCompletion] = useState([]);

  const validationSchema = yup.object().shape({
    worktypeForCompletion: showWorktypeSelection
      ? yup.string().required('Worktype is required')
      : yup.string()
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTasksForCompletion([]);
      setSelectedWorktypeForCompletion('');
      setFilteredTasksForCompletion(showWorktypeSelection ? [] : tasks);
      reset();
    }
  }, [isOpen, showWorktypeSelection, tasks, reset]);

  const handleWorktypeChangeForCompletion = (worktypeId) => {
    setSelectedWorktypeForCompletion(worktypeId);
    setSelectedTasksForCompletion([]);

    if (worktypeId) {
      // Filter tasks by selected worktype
      const filtered = tasks.filter((task) => task.workType_id === Number(worktypeId));
      setFilteredTasksForCompletion(filtered);
    } else {
      setFilteredTasksForCompletion([]);
    }
  };

  const handleTaskSelectionForCompletion = (taskId, checked) => {
    if (checked) {
      setSelectedTasksForCompletion((prev) => [...prev, taskId]);
    } else {
      setSelectedTasksForCompletion((prev) => prev.filter((id) => id !== taskId));
    }
  };

  const handleSelectAllForCompletion = (checked) => {
    if (checked) {
      const allTaskIds = filteredTasksForCompletion.map((task) => task.id);
      setSelectedTasksForCompletion(allTaskIds);
    } else {
      setSelectedTasksForCompletion([]);
    }
  };

  const handleBulkCompleteSubmit = async () => {
    if (selectedTasksForCompletion.length === 0) {
      return;
    }

    if (showWorktypeSelection && !selectedWorktypeForCompletion) {
      return;
    }

    const payload = {
      taskIds: selectedTasksForCompletion,
      ...(showWorktypeSelection && { worktype_id: Number(selectedWorktypeForCompletion) }),
      earlyUpdate: true,
      status: 'Completed'
    };

    await onBulkComplete(payload);
  };

  const displayTasks = showWorktypeSelection ? filteredTasksForCompletion : tasks;

  return (
    <Modal isDirty={true} width={50} name={title} onClose={onClose}>
      <div className="space-y-4 relative">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-800 mb-3">
            {showWorktypeSelection
              ? 'Select worktype and tasks to mark as complete'
              : 'Select tasks to mark as complete'}
          </h3>

          {/* Worktype Selection - only show if enabled */}
          {showWorktypeSelection && (
            <div className="mb-4">
              <FormInput
                width={100}
                label="Select Worktype"
                register={register}
                register_key="worktypeForCompletion"
                isRequired={true}
                type="dropdown"
                options={[
                  { label: 'Select Worktype', value: '', disabled: true, selected: true },
                  ...workTypes
                ]}
                onDropdownChange={handleWorktypeChangeForCompletion}
                validation={{ required: 'Worktype is required' }}
                errors={errors}
              />
            </div>
          )}

          {/* Select All Checkbox - show when worktype is selected (if enabled) or always (if worktype selection disabled) */}
          {(!showWorktypeSelection || selectedWorktypeForCompletion) && (
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="selectAll"
                checked={
                  selectedTasksForCompletion.length === displayTasks.length &&
                  displayTasks.length > 0
                }
                onChange={(e) => handleSelectAllForCompletion(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
              />
              <label htmlFor="selectAll" className="text-sm font-medium text-gray-700">
                Select All ({displayTasks.length} tasks)
              </label>
            </div>
          )}
        </div>

        {/* Tasks List - show when worktype is selected (if enabled) or always (if worktype selection disabled) */}
        {(!showWorktypeSelection || selectedWorktypeForCompletion) && (
          <div className="max-h-[45vh] overflow-y-auto border border-gray-200 rounded-lg">
            {displayTasks.length > 0 ? (
              displayTasks.map((task, index) => (
                <div
                  key={task.id}
                  className="flex items-center gap-3 p-3 border-b border-gray-100 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    id={`task-${task.id}`}
                    checked={selectedTasksForCompletion.includes(task.id)}
                    onChange={(e) => handleTaskSelectionForCompletion(task.id, e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded"
                  />
                  <label
                    htmlFor={`task-${task.id}`}
                    className="flex-1 cursor-pointer text-sm text-gray-800">
                    <div className="font-medium">{task.name}</div>
                    <div className="text-xs text-gray-500">Status: {task.status}</div>
                  </label>
                </div>
              ))
            ) : (
              <div className="p-4 text-center text-gray-500">
                {showWorktypeSelection
                  ? 'No tasks found for the selected worktype'
                  : 'No tasks available'}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleBulkCompleteSubmit}
            disabled={
              selectedTasksForCompletion.length === 0 ||
              (showWorktypeSelection && !selectedWorktypeForCompletion) ||
              isSubmitting
            }>
            Complete ({selectedTasksForCompletion.length})
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkCompleteModal;
